import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { SparkCreatorVideoDto } from '@/app/common/general-types/spark-ads-v3.dto';
import { FormatService } from '@/app/common/format/format.service';
import {
  formatPostedAt,
  libraryStatusFor,
  libraryStatusLabel,
  libraryStatusPillClass,
} from '../../creator-videos.helpers';

/**
 * One post in the creator video library.
 *
 * Note there is no caption: the TikTok One report exposes creator and video metadata
 * but not the post's text, so the card shows the creator and posting date instead of
 * inventing one.
 */
@Component({
  selector: 'sa-video-card',
  imports: [],
  templateUrl: './video-card.html',
  styleUrl: './video-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoCard {
  readonly entry = input.required<SparkCreatorVideoDto>();

  readonly open = output<SparkCreatorVideoDto>();

  private readonly formatService = inject(FormatService);

  protected readonly status = computed(() => libraryStatusFor(this.entry()));
  protected readonly statusLabel = computed(() => libraryStatusLabel(this.status()));
  protected readonly statusPillClass = computed(() => libraryStatusPillClass(this.status()));

  protected readonly handle = computed(() => {
    const handle = this.entry().handle ?? this.entry().video.creator_info?.handle_name;
    if (!handle) {
      return 'Unknown creator';
    }
    return handle.startsWith('@') ? handle : `@${handle}`;
  });

  protected readonly displayName = computed(
    () => this.entry().video.creator_info?.display_name ?? '',
  );

  protected readonly thumbnailUrl = computed(
    () => this.entry().video.video_info?.thumbnail_url ?? '',
  );

  protected readonly views = computed(() =>
    this.formatService.convertIntToString(this.entry().video.video_views ?? 0),
  );

  protected readonly engagementRate = computed(() => {
    const rate = this.entry().video.engagement_rate;
    return typeof rate === 'number' ? `${rate.toFixed(1)}%` : '—';
  });

  protected readonly postedAt = computed(() =>
    formatPostedAt(this.entry().video.video_info?.create_time),
  );

  protected onOpen(): void {
    this.open.emit(this.entry());
  }
}
